import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import CheckBox, { CheckboxSc } from 'components/final-form/CheckBox';
import DatePicker from 'components/final-form/DatePicker';
import { DropdownOption, stringArrayToDropdownOptionArray } from 'components/final-form/dropdownUtils';
import Input from 'components/final-form/Input';
import Radio from 'components/final-form/Radio';
import { generateRadioButtonOptionsFromEnum } from 'components/final-form/radioUtils';
import Select from 'components/final-form/Select';
import PraxFormGrid from 'components/layout/PraxFormGrid';
import PxGrid from 'components/layout/PxGrid';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import { FormApi, FormState } from 'final-form';
import _ from 'lodash';
import moment from 'moment';
import React, { useEffect } from "react";
import { Field, Form as FinalForm, FormRenderProps, FormSpy } from 'react-final-form';
import { MessageSourceProps, withMessages } from "react-message-source";
import { Button, Grid, Header, RadioProps } from 'semantic-ui-react';
import styled from 'styled-components';
import { BillStatus, TreatmentsOrderBy, TreatmentStatus } from 'ts-types/api.enums';
import { ProductDto, TariffDto, TreatmentOverviewSearchRequest } from 'ts-types/api.types';
import { noop } from 'utils/functionUtils';
import { IndexSignature } from 'utils/praxTypes';

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  padding: 1em 1em;

  .ui.buttons {
    margin-left: 10px !important;
  }

  .field {
    display: inline-block !important;
    min-width: 16rem;
  }
`;

const StyledContainer = styled.div`

  flex: 1 1 auto;

  .ui.header:not(h1):not(h2):not(h3):not(h4):not(h5):not(h6) {
    font-size: 1.1rem;
    color: #373737;

    &.small {
      font-size: 1.05em;
    }
  }

  .ui.header.tiny {
    font-size: 1em !important;
  }
`;

const TableContainer = styled.div`
  flex: 1 1 auto;
  min-height: 172px;
  max-width: 300px;
`;

const StyledTable = styled(VirtualizedTable)`
  .ReactVirtualized__Table__Grid {
    outline: none;
    border: 1px solid rgba(34, 36, 38, .15);
    border-top: none;
    border-radius: 0 0 .28571429rem .28571429rem;
  }

  .ReactVirtualized__Table__row {
    border-left: none;
  }

  .ReactVirtualized__Table__headerColumn {
    height: 100%;
    border-right: 1px solid rgba(34, 36, 38, .15);
    padding-top: 0.85rem;
  }

  .ReactVirtualized__Table__headerColumn:last-child {
    border-right: none;
  }

  .ReactVirtualized__Table__rowColumn {
    height: 100%;
    border-right: 1px solid rgba(34, 36, 38, .15);
    padding-top: 0.85rem;
  }

  .ReactVirtualized__Table__rowColumn:last-child {
    border-right: none;
  }
`;

const StyledLabel = styled.label`
  display: inline-block;
  min-width: 75px !important;
  margin-right: 10px;

  &.margin + .margin {
    margin-left: 10px;
  }
`;

const SearchOptionsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

const SearchInputGroup = styled.div`
  flex: 1 1 40%;
`;

const SearchGroup = styled.div`
  flex: 1 1 30%;
`;

const SearchButtonContainer = styled.div`
  flex: 1 1 100%;
  margin-top: 1rem;
`;

const SearchField = styled.div`
  margin-bottom: 7px;
`;

interface Props extends MessageSourceProps, AuthConsumerRenderProps {
  searchTreatments: (exportToFile?: boolean) => void,
  searchData: Partial<TreatmentOverviewSearchRequest>,
  setSearchData: (values: Partial<TreatmentOverviewSearchRequest>) => void,
  tariffs: TariffDto[],
  products: ProductDto[]
}

const TreatmentOverviewOptionsSegment = (props: Props) => {

  const radioMap: Map<string, RadioProps> = new Map<string, RadioProps>();

  const {
    searchData,
    setSearchData,
    searchTreatments,
    tariffs,
    products,
    options: {workDate},
    currentUser,
    getMessage
  } = props;

  const initialValues = (): Partial<TreatmentOverviewSearchRequest> => {

    const tariffIds = tariffs.map(tariff => tariff.id);

    return {
      fromDate: workDate,
      toDate: workDate,
      selectedTariffIds: tariffIds,
      interrupted: undefined,
      mandateId: currentUser ? currentUser.userConfig.mandateId : undefined,
      orderBy: TreatmentsOrderBy.TREATMENT_DATE,
      onlyWithMissingData: false
    }
  };

  const orderByValues = Object.values(TreatmentsOrderBy);

  const treatmentStatusOptions =
      stringArrayToDropdownOptionArray(Object.values(TreatmentStatus), getMessage, "enum.treatmentStatus");

  const billStatusLabels: string[] = [BillStatus.OPEN, BillStatus.BILL, BillStatus.INTERMEDIATE_BILL];
  const billStatusOptions =
      stringArrayToDropdownOptionArray(billStatusLabels, getMessage, "enum.billStatus");

  const interruptedLabels: IndexSignature = {
    INTERRUPTED: true,
    NOT_INTERRUPTED: false
  };

  let interruptedOptions: DropdownOption[] = Object.keys(interruptedLabels).map(option => (
      {
        key: option,
        text: getMessage(`treatmentOverview.filter.${option}`),
        value: interruptedLabels[option]
      }
  ));

  let productOptions: DropdownOption[] = [
    {
      key: "all",
      text: getMessage("all"),
      value: "all"
    }
  ];

  products.forEach(product => {
    const defaultDesc = !_.isEmpty(product.description) ? product.description : undefined;

    const texts: { [key: string]: string | undefined } = {
      "de": defaultDesc,
      "en": !_.isEmpty(product.descriptionEn) ? product.descriptionEn : defaultDesc,
      "fr": !_.isEmpty(product.descriptionFr) ? product.descriptionFr : defaultDesc,
      "it": !_.isEmpty(product.descriptionIt) ? product.descriptionIt : defaultDesc
    };

    const lang = currentUser ? currentUser.userConfig.language : "de";
    const desc = texts[lang] ? ` - ${texts[lang]}` : "";

    productOptions.push({
      key: product.id,
      text: `${product.id} ${desc}`,
      value: product.id
    })
  });

  const orderBy = generateRadioButtonOptionsFromEnum(orderByValues, "orderBy", getMessage);

  orderBy.forEach((radioDefinition, index) => {
    radioMap.set(orderByValues[index], radioDefinition);
  });

  useEffect(() => {
    const tariffIds = tariffs.map(tariff => tariff.id);
    setSearchData({...initialValues(), selectedTariffIds: tariffIds});
  }, [tariffs]);

  const handleChange = ({values}: FormState, form: FormApi) => {

    const fromDate = values.fromDate && new Date(values.fromDate);
    const toDate = values.toDate && new Date(values.toDate);

    if (fromDate && toDate && fromDate.getTime() > toDate.getTime()) {
      form.change("toDate", moment(fromDate).format("YYYY-MM-DD"));
    }

    setSearchData(form.getState().values);
  };

  const searchForTreatments = (values: Partial<TreatmentOverviewSearchRequest>) => {
    setSearchData(values);
    searchTreatments();
  };

  const selectTariff = (values: TreatmentOverviewSearchRequest, form: FormApi, id: number) => {
    const tariffSelected = values.selectedTariffIds.includes(id);
    if (tariffSelected) {
      const tariffs = values.selectedTariffIds.filter((tariffId: number) => tariffId !== id);
      form.change("selectedTariffIds", tariffs);
    } else {
      const tariffs = [...values.selectedTariffIds, id];
      form.change("selectedTariffIds", tariffs);
    }
  }

  const onEnterKeyPressed = (e: any, values: any) => {
    if (e.keyCode === 13) {
      e.preventDefault();
      searchForTreatments(values)
    }
  };

  const renderForm = ({handleSubmit, values, submitting, form}: FormRenderProps): React.ReactNode => {

    const treatmentDate = radioMap.get(TreatmentsOrderBy.TREATMENT_DATE);
    const patientName = radioMap.get(TreatmentsOrderBy.PATIENT_NAME);
    const guarantorName = radioMap.get(TreatmentsOrderBy.GUARANTOR_NAME);

    const disabledBefore = values.fromDate && new Date(values.fromDate);

    return (
        <StyledForm onSubmit={handleSubmit}>
          <StyledContainer>
            <Header>{getMessage("searchOptions")}</Header>
          </StyledContainer>
          <StyledContainer style={{marginTop: 10}}
                           onKeyDown={(e) => onEnterKeyPressed(e, values)}>
            <SearchOptionsContainer>
              <SearchInputGroup>
                <Header size="tiny">{getMessage("automaticBilling.termination")}</Header>
                <PraxFormGrid>
                  <PxGrid>
                    <Grid.Row>
                      <Grid.Column width={8}>
                        <div>
                          <StyledLabel className="margin">
                            {getMessage("patient.search.checkbox.externalIdManual")}:
                          </StyledLabel>
                          <Field
                              name="externalId"
                              component={Input}
                              fluid
                          />
                        </div>
                      </Grid.Column>
                    </Grid.Row>
                    <Grid.Row>
                      <Grid.Column width={8}>
                        <div>
                          <StyledLabel className="margin">
                            {getMessage("from")}:
                          </StyledLabel>
                          <Field
                              name="fromDate"
                              component={DatePicker}
                              fluid
                          />
                        </div>
                      </Grid.Column>
                      <Grid.Column width={8}>
                        <div>
                          <StyledLabel className="margin">
                            {getMessage("treatmentOverview.interrupted")}:
                          </StyledLabel>
                          <Field
                              name="interrupted"
                              component={Select}
                              options={interruptedOptions}
                              clearable
                              fluid
                          />
                        </div>
                      </Grid.Column>
                    </Grid.Row>
                    <Grid.Row>
                      <Grid.Column width={8}>
                        <div>
                          <StyledLabel className="margin">
                            {getMessage("to")}:
                          </StyledLabel>
                          <Field
                              name="toDate"
                              component={DatePicker}
                              disabledBefore={disabledBefore}
                              fluid
                          />
                        </div>
                      </Grid.Column>
                      <Grid.Column width={8}>
                        <div>
                          <StyledLabel className="margin">
                            {getMessage("product.form.title")}:
                          </StyledLabel>
                          <Field
                              name="productId"
                              component={Select}
                              options={productOptions}
                              clearable
                              fluid
                          />
                        </div>
                      </Grid.Column>
                    </Grid.Row>
                    <Grid.Row>
                      <Grid.Column width={8}>
                        <div>
                          <StyledLabel className="margin">
                            {getMessage("status")}:
                          </StyledLabel>
                          <Field
                              name="treatmentStatus"
                              component={Select}
                              options={treatmentStatusOptions}
                              clearable
                              fluid
                          />
                        </div>
                      </Grid.Column>
                      <Grid.Column width={8}>
                        <div>
                          <StyledLabel className="margin"/>
                          <Field
                              name="onlyWithMissingData"
                              component={CheckBox}
                              label={getMessage('treatmentOverview.onlyWithMissingData')}
                          />
                        </div>
                      </Grid.Column>
                    </Grid.Row>
                    <Grid.Row>
                      <Grid.Column width={8}>
                        <div>
                          <StyledLabel className="margin">
                            {getMessage("mediDataDownload.billStatus")}:
                          </StyledLabel>
                          <Field
                              name="billStatus"
                              component={Select}
                              options={billStatusOptions}
                              clearable
                              fluid
                          />
                        </div>
                      </Grid.Column>
                    </Grid.Row>
                  </PxGrid>
                </PraxFormGrid>
              </SearchInputGroup>
              <SearchGroup>
                <Header size="tiny">{getMessage("automaticBilling.tariffSelection")}</Header>
                <TableContainer>
                  {renderTariffsTable(form, values as TreatmentOverviewSearchRequest, tariffs)}
                </TableContainer>
              </SearchGroup>
              <SearchGroup>
                <Header size="tiny">{getMessage("automaticBilling.orderBy")}</Header>
                <SearchField>
                  <Field
                      name="orderBy"
                      component={Radio}
                      radioDefinition={treatmentDate}
                  />
                </SearchField>
                <SearchField>
                  <Field
                      name="orderBy"
                      component={Radio}
                      radioDefinition={patientName}
                  />
                </SearchField>
                <SearchField>
                  <Field
                      name="orderBy"
                      component={Radio}
                      radioDefinition={guarantorName}
                  />
                </SearchField>
              </SearchGroup>
              <SearchButtonContainer>
                <Button
                    type="button" primary compact
                    onClick={() => searchForTreatments(values)}>
                  {getMessage("search")}
                </Button>
                <Button
                    type="button"
                    compact
                    disabled={submitting}
                    onClick={() => searchTreatments(true)}
                >
                  {getMessage("interventionAvailability.export")}
                </Button>
              </SearchButtonContainer>
            </SearchOptionsContainer>
          </StyledContainer>
          <FormSpy subscription={{values: true}} onChange={formState => handleChange(formState, form)}/>
        </StyledForm>
    )
  };

  const selectHeaderColumn = (form: FormApi, values: TreatmentOverviewSearchRequest, tariffs: Array<TariffDto>) => {

    const countSelectedTariffs = values.selectedTariffIds ? values.selectedTariffIds.length : 0;
    const indeterminate = countSelectedTariffs > 0 && countSelectedTariffs < tariffs.length;
    const noSelectedTariffs = countSelectedTariffs === 0 && tariffs.length > 0;
    const allTariffsSelected = tariffs.length > 0 && countSelectedTariffs === tariffs.length;

    return (
        <CheckboxSc
            checked={countSelectedTariffs > 0}
            indeterminate={indeterminate}
            onChange={
              () => {
                if (noSelectedTariffs || indeterminate) {
                  const selectedTariffs = tariffs.map(tariff => tariff.id);
                  form.change("selectedTariffIds", selectedTariffs);
                }

                if (allTariffsSelected) {
                  form.change("selectedTariffIds", []);
                }

              }
            }
            disabled={tariffs.length === 0}
        />
    )
  };

  const tariffsRowGetter = (tariffs: Array<TariffDto>) => ({index}: any) => {

    if (tariffs[index]) {
      Object.assign(tariffs[index], {index: index + 1});

      return tariffs[index];
    }

    return {
      index: index + 1
    }
  };

  const tariffsRowRenderer = (form: FormApi, values: TreatmentOverviewSearchRequest) => ({
                                                                                           className,
                                                                                           columns,
                                                                                           index,
                                                                                           key,
                                                                                           style,
                                                                                           rowData
                                                                                         }: any) => {

    const a11yProps = {"aria-rowindex": index + 1};

    let styles = {...style, userSelect: "none"};

    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={styles}
            onClick={() => selectTariff(values, form, rowData.id)}
        >
          {columns}
        </div>
    );
  };

  const tariffSelectCellRenderer = (data: any, values: TreatmentOverviewSearchRequest, form: FormApi) => {

    const {rowData} = data;
    const {id} = rowData;

    const tariffSelected = values.selectedTariffIds.includes(id);

    return (
        <CheckboxSc
            checked={tariffSelected}
            onChange={() => selectTariff(values, form, id)}
        />
    )
  };

  const tariffDescriptionCellRenderer = (data: any) => {

    return <div>{data.rowData.description}</div>
  };

  const renderTariffsTable = (form: FormApi, values: TreatmentOverviewSearchRequest, tariffs: Array<TariffDto>): React.ReactNode => {

    return (
        <StyledTable
            rowGetter={tariffsRowGetter(tariffs)}
            rowRenderer={tariffsRowRenderer(form, values)}
            rowCount={tariffs.length}
            columns={[
              {
                width: 30,
                label: selectHeaderColumn(form, values, tariffs),
                dataKey: "id",
                cellRenderer: (data: any) => tariffSelectCellRenderer(data, values, form)
              },
              {
                width: 180,
                label: getMessage("description"),
                dataKey: "description",
                cellRenderer: tariffDescriptionCellRenderer
              }
            ]}
        />
    )
  };

  return (
      <FinalForm
          onSubmit={() => noop}
          initialValues={searchData}
          subscription={{values: true, pristine: true}}
          render={renderForm}
      />
  );

}

export default withAuthContext(withMessages(TreatmentOverviewOptionsSegment));
